import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, User, Mail, Phone, Building2, AtSign, Lock, Trash2, Plus } from 'lucide-react';
import Logo from '@/components/Logo';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Profile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [clubs, setClubs] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    phone: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [deletionReason, setDeletionReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isRequestingDeletion, setIsRequestingDeletion] = useState(false);
  const [myClubs, setMyClubs] = useState([]);
  const [myClubRequests, setMyClubRequests] = useState([]);
  const [isRequestingClub, setIsRequestingClub] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load clubs first, then profile to ensure clubs are available when setting form data
      const clubsResponse = await axios.get(`${API}/clubs`, { withCredentials: true });
      const clubsList = clubsResponse.data || [];
      setClubs(clubsList);
      
      const profileResponse = await axios.get(`${API}/users/profile`, { withCredentials: true });
      setUser(profileResponse.data);
      
      // Load user's clubs and pending requests
      const myClubsResponse = await axios.get(`${API}/users/my-clubs`, { withCredentials: true });
      setMyClubs(myClubsResponse.data || []);
      
      const myRequestsResponse = await axios.get(`${API}/users/my-club-requests`, { withCredentials: true });
      setMyClubRequests(myRequestsResponse.data || []);
      
      // Check if user's club exists in the clubs list
      setFormData({
        name: profileResponse.data.name || '',
        nickname: profileResponse.data.nickname || '',
        phone: profileResponse.data.phone || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestClub = async (clubId, clubName) => {
    setIsRequestingClub(true);
    try {
      await axios.post(`${API}/users/club-request/${clubId}`, {}, { withCredentials: true });
      toast.success(`Anfrage für ${clubName} gesendet`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Anfrage fehlgeschlagen');
    } finally {
      setIsRequestingClub(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/users/club-request/${requestId}`, { withCredentials: true });
      toast.success('Anfrage zurückgezogen');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Zurückziehen der Anfrage');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSaving(true);
    try {
      await axios.put(`${API}/users/profile`, formData, { withCredentials: true });
      toast.success('Profile updated successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsChangingPassword(true);
    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleRequestDeletion = async () => {
    setIsRequestingDeletion(true);
    try {
      await axios.post(`${API}/users/request-deletion`, {
        reason: deletionReason || 'No reason provided'
      }, { withCredentials: true });
      toast.success('Deletion request sent to administrators');
      setDeletionReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit deletion request');
    } finally {
      setIsRequestingDeletion(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#007AFF]"></div>
          <p className="mt-4 text-white">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <Button 
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('backToDashboard')}
          </Button>
          <Logo size="lg" />
        </div>

        <Card className="bg-[#111111] border-[#27272A]">
          <CardHeader>
            <CardTitle className="text-white text-2xl font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>
              {t('yourProfile')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-20 h-20 bg-[#007AFF]/20 rounded-full flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt="Profile" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-[#007AFF]" />
                )}
              </div>
              <div>
                <h3 className="text-xl font-bold">{user?.name}</h3>
                <p className="text-gray-400">{user?.email}</p>
              </div>
            </div>

            <Tabs defaultValue="profile" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6 bg-[#0A0A0A]">
                <TabsTrigger value="profile">{t('profileInformationTab')}</TabsTrigger>
                <TabsTrigger value="clubs">{t('myClubs')}</TabsTrigger>
                <TabsTrigger value="security">{t('securityTab')}</TabsTrigger>
              </TabsList>

              <TabsContent value="profile">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name" className="text-white flex items-center gap-2">
                      <User className="w-4 h-4" />
                      {t('name')}
                    </Label>
                    <Input
                      id="name"
                      data-testid="profile-name-input"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="nickname" className="text-white flex items-center gap-2">
                      <AtSign className="w-4 h-4" />
                      {t('nickname')} <span className="text-gray-500">{t('optional')}</span>
                    </Label>
                    <Input
                      id="nickname"
                      data-testid="profile-nickname-input"
                      type="text"
                      placeholder="Your game nickname"
                      value={formData.nickname}
                      onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-white flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {t('phone')} <span className="text-gray-500">{t('optional')}</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+43 123 456 789"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                    />
                  </div>

                  <div className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4">
                    <Label className="text-white flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4" />
                      {t('email')}
                    </Label>
                    <p className="text-gray-400">{user?.email}</p>
                    <p className="text-sm text-gray-500 mt-1">{t('contactAdmin')}</p>
                  </div>

                  <Button 
                    type="submit"
                    data-testid="save-profile-btn"
                    disabled={isSaving}
                    className="w-full bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full font-bold py-6"
                  >
                    {isSaving ? t('saving') : t('save')}
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="clubs">
                <div className="space-y-6">
                  {/* Current Clubs */}
                  <div>
                    <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      {t('myCurrentClubs')}
                    </h3>
                    {myClubs.length === 0 ? (
                      <p className="text-gray-400">{t('noClubsYet')}</p>
                    ) : (
                      <div className="space-y-2">
                        {myClubs.map(club => (
                          <div 
                            key={club.club_id}
                            className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-4 flex items-center gap-3"
                          >
                            {club.logo_url ? (
                              <img src={club.logo_url} alt={club.name} className="w-10 h-10 object-contain rounded" />
                            ) : (
                              <div className="w-10 h-10 bg-[#27272A] rounded flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-gray-500" />
                              </div>
                            )}
                            <span className="text-white flex-1">{club.name}</span>
                            {club.is_club_admin && (
                              <span className="text-xs bg-[#34C759]/20 text-[#34C759] px-2 py-1 rounded-full">
                                {t('clubAdmin')}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pending Requests */}
                  {myClubRequests.filter(r => r.status === 'pending').length > 0 && (
                    <div>
                      <h3 className="text-white font-bold mb-4">{t('pendingRequests')}</h3>
                      <div className="space-y-2">
                        {myClubRequests.filter(r => r.status === 'pending').map(req => (
                          <div 
                            key={req.request_id}
                            className="bg-[#0A0A0A] border border-[#FF9500]/50 rounded-lg p-4 flex items-center justify-between"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-[#FF9500]/20 rounded flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-[#FF9500]" />
                              </div>
                              <div>
                                <span className="text-white">{req.club_name}</span>
                                <p className="text-xs text-[#FF9500]">{t('awaitingApproval')}</p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelRequest(req.request_id)}
                              className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                            >
                              {t('cancel')}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Request to Join New Club */}
                  <div>
                    <h3 className="text-white font-bold mb-4">{t('joinAnotherClub')}</h3>
                    <p className="text-gray-400 text-sm mb-4">{t('joinClubDescription')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {clubs
                        .filter(club => {
                          // Filter out clubs user is already in or has pending request for
                          const isAlreadyMember = myClubs.some(mc => mc.club_id === club.club_id);
                          const hasPendingRequest = myClubRequests.some(r => r.club_id === club.club_id && r.status === 'pending');
                          return !isAlreadyMember && !hasPendingRequest;
                        })
                        .map(club => (
                          <div 
                            key={club.club_id}
                            className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 flex items-center gap-3"
                          >
                            {club.logo_url ? (
                              <img src={club.logo_url} alt={club.name} className="w-8 h-8 object-contain rounded" />
                            ) : (
                              <div className="w-8 h-8 bg-[#27272A] rounded flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-gray-500" />
                              </div>
                            )}
                            <span className="text-white flex-1 text-sm">{club.name}</span>
                            <Button
                              size="sm"
                              onClick={() => handleRequestClub(club.club_id, club.name)}
                              disabled={isRequestingClub}
                              className="bg-[#007AFF] hover:bg-[#0066D6] text-white rounded-full text-xs"
                            >
                              {t('requestToJoin')}
                            </Button>
                          </div>
                        ))}
                    </div>
                    {clubs.filter(club => {
                      const isAlreadyMember = myClubs.some(mc => mc.club_id === club.club_id);
                      const hasPendingRequest = myClubRequests.some(r => r.club_id === club.club_id && r.status === 'pending');
                      return !isAlreadyMember && !hasPendingRequest;
                    }).length === 0 && (
                      <p className="text-gray-500 text-center py-4">{t('alreadyInAllClubs')}</p>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="security">
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <Label htmlFor="current_password" className="text-white flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('currentPassword')}
                    </Label>
                    <Input
                      id="current_password"
                      data-testid="current-password-input"
                      type="password"
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="new_password" className="text-white flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('newPassword')}
                    </Label>
                    <Input
                      id="new_password"
                      data-testid="new-password-input"
                      type="password"
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      required
                      minLength={6}
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('minimum6Chars')}</p>
                  </div>

                  <div>
                    <Label htmlFor="confirm_password" className="text-white flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {t('confirmPassword')}
                    </Label>
                    <Input
                      id="confirm_password"
                      data-testid="confirm-password-input"
                      type="password"
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      className="bg-[#0A0A0A] border-[#27272A] text-white"
                      required
                    />
                  </div>

                  <Button 
                    type="submit"
                    data-testid="change-password-btn"
                    disabled={isChangingPassword}
                    className="w-full bg-[#FF3B30] hover:bg-[#D6301E] text-white rounded-full font-bold py-6"
                  >
                    {isChangingPassword ? t('changingPassword') : t('changePassword')}
                  </Button>
                </form>

                {/* Account Deletion Section */}
                <div className="mt-8 pt-8 border-t border-[#27272A]">
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    <Trash2 className="w-5 h-5 text-[#FF3B30]" />
                    {t('deleteAccount')}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">
                    {t('deleteAccountDescription')}
                  </p>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        data-testid="request-deletion-btn"
                        variant="outline"
                        className="border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-full"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Request Account Deletion
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-[#111111] border-[#27272A]">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Request Account Deletion</AlertDialogTitle>
                        <AlertDialogDescription className="text-gray-400">
                          Your request will be sent to administrators for review. 
                          Once approved, your account will be deleted and your match history will be anonymized.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="py-4">
                        <Label htmlFor="deletion-reason" className="text-white mb-2 block">
                          Reason for deletion (optional)
                        </Label>
                        <textarea
                          id="deletion-reason"
                          data-testid="deletion-reason-input"
                          placeholder="Tell us why you want to delete your account..."
                          value={deletionReason}
                          onChange={(e) => setDeletionReason(e.target.value)}
                          rows={3}
                          className="w-full bg-[#0A0A0A] border border-[#27272A] text-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF3B30]"
                        />
                      </div>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="border-[#27272A] hover:bg-white/10 text-white">
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleRequestDeletion}
                          disabled={isRequestingDeletion}
                          className="bg-[#FF3B30] hover:bg-[#D6301E]"
                        >
                          {isRequestingDeletion ? 'Sending...' : 'Submit Request'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Profile;